import { sortBy, uniq, flatten, pick, compact, forOwn } from "lodash";
import {
	GEOGRAPHY_LEVELS,
	OFFER_PRICE_TYPES,
	PRODUCT_EXPERIENCES,
	PRODUCTS_FILTERS_KEY,
} from "app/constants";
import { FacetedClassification, FacetedQuery } from "immfacet";
import { fromJS } from "immutable";
import { getDayCount } from "app/utils/utils";

export const computeMonthFilterValues = (products = []) => {
	const months = products.map(product => {
		if (!product.months || product.months.length === 0) {
			return [];
		}
		return product.months || [];
	});

	const sortedMonths = sortBy(flatten(months), "startAt");

	const labels = sortedMonths.map(month => {
		return String(pick(month, "startAt").startAt) || [];
	});

	return uniq(labels);
};

export const computeDestinationFilterValues = (products = []) => {
	if (!products?.length) {
		return [];
	}

	const toSortedUniqList = arr => sortBy(uniq(flatten(arr)));

	const countryDestinationsList = toSortedUniqList(products.map(({ country }) => country || []));

	if (countryDestinationsList?.length > 1) {
		return countryDestinationsList;
	}

	const resortDestinationsList = toSortedUniqList(
		products.map(({ resort, productExperience }) => {
			if (!resort) {
				return [];
			}

			return ![
				PRODUCT_EXPERIENCES.CIRCUIT,
				PRODUCT_EXPERIENCES.AUTOTOUR,
				PRODUCT_EXPERIENCES.CRUISE,
			].includes(productExperience)
				? resort
				: "listing.filter.destination.multi.destinations.label";
		})
	);

	return resortDestinationsList;
};

export const computeZoneFilterValues = (products = []) => {
	const zones = products.map(product => {
		if (!product.zones || product.zones.length === 0) {
			return [];
		}
		return product.zones || [];
	});
	return sortBy(uniq(flatten(zones)));
};

export const computeBadgeFilterValues = (products = []) => {
	const badges = products.map(product => {
		if (!product.badges || product.badges.length === 0) {
			return [];
		}
		return product.badges || [];
	});

	const firstBadges = badges.map(badgeList => {
		return badgeList[0];
	});

	const sortedBadges = sortBy(flatten(compact(firstBadges)), "label");

	const badgeLabels = sortedBadges.map(badge => {
		return pick(badge, "label").label || [];
	});

	return uniq(badgeLabels);
};

export const computeTopicFilterValues = (products = []) => {
	if (!products || products.length === 0) {
		return [];
	}

	const topics = products.map(product => {
		return product.topics || [];
	});

	return sortBy(uniq(flatten(topics)));
};

export const computeMerchandisingTopicsFilterValues = (products = []) => {
	if (!products || products.length === 0) {
		return [];
	}

	const merchandisingTopics = products.map(product => {
		return product.merchandisingTopics || [];
	});

	return sortBy(uniq(flatten(merchandisingTopics)));
};

export const generateFacetCollection = (products = [], productsFilters = {}) => {
	let facetCollection = new FacetedClassification(fromJS(products));
	const countryDestinationsList = sortBy(
		uniq(flatten(products?.map(({ country }) => country || [])))
	);
	const levelToCheck =
		countryDestinationsList?.length <= 1 ? GEOGRAPHY_LEVELS.RESORT : GEOGRAPHY_LEVELS.COUNTRY;

	facetCollection = facetCollection
		.addFacet(
			PRODUCTS_FILTERS_KEY.TOPIC,
			product => {
				if (!product.get("topics")) {
					return undefined;
				}
				return product.get("topics").toJS();
			},
			{
				multiValue: true,
			}
		)
		.addFieldFacet(PRODUCTS_FILTERS_KEY.MERCHANDISINGS, {
			multiValue: true,
		})
		.addFacet(PRODUCTS_FILTERS_KEY.DESTINATION, product => {
			if (!product.get(levelToCheck)) {
				return undefined;
			}
			return product.get(levelToCheck);
		})
		.addFacet(
			PRODUCTS_FILTERS_KEY.ZONE,
			product => {
				if (!product.get("zones")) {
					return undefined;
				}
				return product
					.get("zones")
					.toJS()
					.map(zone => {
						return zone;
					});
			},
			{
				multiValue: true,
			}
		)
		.addFacet(
			PRODUCTS_FILTERS_KEY.CATEGORY,
			product => {
				if (!product.get("merchandisingTopics")) {
					return undefined;
				}
				return product.get("merchandisingTopics").toJS();
			},
			{
				multiValue: true,
			}
		)
		.addFacet(
			PRODUCTS_FILTERS_KEY.BADGE,
			product => {
				if (!product.get("badges")) {
					return undefined;
				}
				return product
					.get("badges")
					.toJS()
					.map(badge => {
						return badge.label;
					});
			},
			{
				multiValue: true,
			}
		)
		.addFacet(
			PRODUCTS_FILTERS_KEY.MONTH,
			product => {
				if (!product.get("months")) {
					return undefined;
				}
				return product
					.get("months")
					.toJS()
					.map(month => {
						return String(month.startAt);
					});
			},
			{
				multiValue: true,
			}
		)
		.addFacet(PRODUCTS_FILTERS_KEY.START_AT, product => {
			if (!product.get("startAt")) {
				return undefined;
			}
			return String(
				getDayCount(product.get("startAt"), Date.now()) <= 2 &&
					getDayCount(product.get("startAt"), Date.now()) >= 0
			);
		})
		.addFacet(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE, product => {
			if (!product.get("fromPriceType")) {
				return undefined;
			}

			return String(
				product.get("fromPriceType").toJS().type ===
					OFFER_PRICE_TYPES.FROM_PRICE_TYPE_SAVE_UP_TO
					? Number(product.get("fromPriceType").toJS().value) >= 50
					: undefined
			);
		});

	let facetQuery = new FacetedQuery(facetCollection);

	forOwn(productsFilters, (value, key) => {
		// on doit gérer les valeurs en string et boolean (pour startAt et endAt)
		if ((value && value.length > 0) || (value && typeof value === "boolean")) {
			facetQuery = facetQuery.select({
				name: key,
				values: typeof value === "string" || typeof value === "boolean" ? [value] : value,
			});
		}
	});

	return facetQuery;
};
